// Подписи значений для людей: состояния, операции матрицы прав, действия журнала, типы записей.
// Словари журнала — общие с выгрузкой в Excel, в @kp/contracts (сценарий 4.3, FR-033): экран
// и файл подписывают записи одинаково. Здесь — то, что нужно только интерфейсу: цвета бейджей
// и роли во фразе. Термины — по словарю docs/glossary.md (FR-036б); тексты не зависят от рода
// человека (FR-036а).
import { roleInfo } from '@kp/contracts';
import type {
  Area,
  MailChannelState,
  MailConnectionState,
  MailMessageState,
  Role,
  SubstitutionState,
  SupportGrantState,
  UserState,
} from '@kp/contracts';
import { areaKindLabels } from '@kp/contracts/audit/labels';
import type { BadgeTone } from '@kp/ui';

export {
  areaKindLabels,
  auditActionLabels,
  auditActorName,
  auditEntityLabel,
  auditOperationLabel,
  auditResultLabels,
  auditSourceLabels,
  entityTypeLabel,
  entityTypeLabels,
  fieldLabel,
  fieldLabels,
  operationLabels,
  purposeLabels,
  reasonLabels,
  substitutionStateLabels,
  supportGrantStateLabels,
  userIdFields,
  userStateLabels,
} from '@kp/contracts/audit/labels';

/** Цвет состояния дублируется словом (README «Цвет»). */
export const userStateTones = {
  invited: 'neutral',
  active: 'ok',
  locked: 'bad',
  archived: 'neutral',
} as const satisfies Record<UserState, BadgeTone>;

export const substitutionStateTones = {
  planned: 'accent',
  active: 'ok',
  ended: 'neutral',
  cancelled: 'neutral',
} as const satisfies Record<SubstitutionState, BadgeTone>;

export const supportGrantStateTones = {
  active: 'warn',
  expired: 'neutral',
  revoked: 'neutral',
} as const satisfies Record<SupportGrantState, BadgeTone>;

// 008: канал почты — состояние подключения, статус письма, канал на панели состояния. Ошибка —
// color-bad, работает и доставлено — color-ok, ждёт и отключено — нейтрально и color-warn.
export const mailConnectionStateTones = {
  not_configured: 'neutral',
  checking: 'neutral',
  working: 'ok',
  failing: 'bad',
} as const satisfies Record<MailConnectionState, BadgeTone>;

export const mailMessageStateTones = {
  queued: 'neutral',
  sent: 'accent',
  sent_unknown: 'accent',
  delivered: 'ok',
  failed: 'bad',
  // 033: письмо о пунктах собрано, но не ушло.
  shadow: 'neutral',
} as const satisfies Record<MailMessageState, BadgeTone>;

export const mailChannelStateTones = {
  not_configured: 'neutral',
  working: 'ok',
  failing: 'bad',
  disabled: 'warn',
  // 033: «Остановить все сообщения».
  stopped: 'bad',
} as const satisfies Record<MailChannelState, BadgeTone>;

export function roleTitle(role: Role): string {
  return roleInfo[role].title;
}

/** Роли внутри фразы: «Контролёр, ведущий». */
export function rolesText(roles: readonly Role[]): string {
  const text = roles.map((role) => roleInfo[role].inText).join(', ');
  return text ? text.charAt(0).toLocaleUpperCase('ru-RU') + text.slice(1) : '';
}

export function areaLabel(area: Area): string {
  return area.kind === 'installation'
    ? areaKindLabels.installation
    : `${areaKindLabels[area.kind]} ${area.refId.slice(0, 8)}`;
}
